import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { Tabs } from 'radix-ui';
import * as React from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import {
	invalidateIntegrationQuery,
	useCallbackAuthorizeUrlMutation,
	useGetExternalIntegrationQuery,
} from '../../../../queries';
import { useDisconnectUserFromIntegrationMutation } from '../../../../queries/Integrations/useDisconnectUserFromIntegrationMutation';
import bloomerangLogo from '../../../assets/logo_bloomerang.svg';
import { Button } from '../../../components-v2/Button';
import { THEME } from '../../../components-v2/Theme/theme';
import { DisconnectSyncUserModal } from '../../../components/integrations/DisconnectSyncUserModal';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { InProgressIcon } from '../../../components/svgs/icons/InProgressIcon';
import { SuccessCheckIcon } from '../../../components/svgs/icons/SuccessCheckIcon';
import { bs } from '../../../styles/styles';
import { styleSheet as integrationsStyleSheet } from '../styles';
import { styleSheet } from './styles';

enum TabValues {
	Connect = 'connect',
	Manage = 'manage',
}

export const Bloomerang: React.FC = () => {
	const history = useHistory();
	const userSession = useUserSession();
	const params = useParams<{ tab?: string }>();
	const [isDisconnectModalOpen, setIsDisconnectModalOpen] = React.useState(false);

	const integrationQuery = useGetExternalIntegrationQuery({
		provider: Api.IntegrationProvider.Bloomerang,
	});

	const disconnectMutation = useDisconnectUserFromIntegrationMutation({
		onError: error => {
			toast.error(error.systemMessage);
		},
		onSuccess: () => {
			toast.success('Integration disabled successfully');
			invalidateIntegrationQuery({ provider: Api.IntegrationProvider.Bloomerang });
			history.push('/integrations');
		},
	});

	const authorizeUrlMutation = useCallbackAuthorizeUrlMutation({
		onError: error => {
			toast.error(error.systemMessage);
		},
		onSuccess: url => {
			if (!url) {
				toast.error('Unable to start the Bloomerang connection. Please try again.');
				return;
			}

			window.location.replace(url);
		},
	});

	const changeTab = React.useCallback(
		(value: string) => {
			history.replace(`/integrations/${Api.IntegrationProvider.Bloomerang}/${value}`);
		},
		[history]
	);

	const userConnection = integrationQuery.data?.configuration?.userConnections?.find(
		connection => connection.userId === userSession.user.id
	);
	const isConnected = !!userConnection?.isConnected;

	const defaultTab = isConnected ? TabValues.Manage : TabValues.Connect;
	const activeTab = params.tab === TabValues.Connect || params.tab === TabValues.Manage ? params.tab : defaultTab;
	const urlHasValidTab = activeTab === params.tab;

	// Only fills in a missing or unrecognized tab segment. Mirroring state into the URL instead would
	// make the Connect tab — and so Disconnect — unreachable for a connected user.
	React.useEffect(() => {
		if (!integrationQuery.data || urlHasValidTab) {
			return;
		}

		changeTab(defaultTab);
	}, [integrationQuery.data, urlHasValidTab, defaultTab, changeTab]);

	const onDisconnect = () => {
		disconnectMutation.mutate(Api.ExternalIntegrationType.Bloomerang);
	};

	if (integrationQuery.isLoading) {
		return <LoadingSpinner type='large' className={css(bs.absoluteCenter)} />;
	}

	return (
		<div className={css(styleSheet.container)}>
			<figure className={css(integrationsStyleSheet.logo, integrationsStyleSheet.headerLogo)}>
				<img src={bloomerangLogo} alt='Bloomerang Logo' />
			</figure>
			<p className={css(integrationsStyleSheet.bodyText, integrationsStyleSheet.description)}>
				Levitate can sync your Bloomerang constituents into contacts, including names, email addresses, phone numbers,
				mailing addresses and households.
			</p>
			<Tabs.Root className={`tab-view ${css(styleSheet.tabView)}`} value={activeTab} onValueChange={changeTab}>
				<Tabs.List className={`tab-view-tabbar-tabs ${css(styleSheet.tabViewTabs)}`}>
					<Tabs.Trigger
						value={TabValues.Connect}
						className={`tab-view-tabbar-item ${activeTab === TabValues.Connect ? `tabbar-item-selected ${css(styleSheet.tabItemSelected)}` : ''} ${css(styleSheet.tabViewItem)}`}
					>
						<SuccessCheckIcon
							fillColor={isConnected ? undefined : THEME.colors.icon.disabled}
							ringColor={isConnected ? undefined : THEME.colors.background.main}
						/>
						<span>Connect</span>
					</Tabs.Trigger>

					<Tabs.Trigger
						value={TabValues.Manage}
						className={`tab-view-tabbar-item ${activeTab === TabValues.Manage ? `tabbar-item-selected ${css(styleSheet.tabItemSelected)}` : ''} ${css(styleSheet.tabViewItem)}`}
					>
						<span>Manage</span>
					</Tabs.Trigger>
				</Tabs.List>

				<Tabs.Content value={TabValues.Connect} className='tab-view-content'>
					{isConnected ? (
						<div className={css(styleSheet.section)}>
							<p className={css(integrationsStyleSheet.bodyText)}>
								If you wish to disable this integration for your user, click Disconnect.
							</p>
							<Button
								isLoading={disconnectMutation.isPending}
								onClick={() => {
									if (integrationQuery.data?.configuration?.syncUserId === userSession.user.id) {
										setIsDisconnectModalOpen(true);
									} else {
										onDisconnect();
									}
								}}
							>
								Disconnect
							</Button>
						</div>
					) : (
						<div className={css(styleSheet.section)}>
							<div className={css(styleSheet.inProgressIconContainer)}>
								<InProgressIcon />
							</div>
							<p className={css(integrationsStyleSheet.bodyText)}>
								To connect your user to the account, click Connect.
							</p>
							<div>
								<Button
									isLoading={authorizeUrlMutation.isPending}
									onClick={() => authorizeUrlMutation.mutate({ provider: Api.IntegrationProvider.Bloomerang })}
								>
									Connect
								</Button>
							</div>
						</div>
					)}
				</Tabs.Content>

				<Tabs.Content value={TabValues.Manage} className='tab-view-content'>
					<div className={css(styleSheet.section)}>
						<p className={css(integrationsStyleSheet.bodyText)}>
							Once you are connected, your Bloomerang data will show up in the contacts tab.
						</p>
						<Button asChild>
							<Link to='/people'>Contacts</Link>
						</Button>
					</div>
				</Tabs.Content>
			</Tabs.Root>

			<DisconnectSyncUserModal
				integrationDisplayName='Bloomerang'
				isDisableModalOpen={isDisconnectModalOpen}
				onDisable={onDisconnect}
				setIsDisableModalOpen={setIsDisconnectModalOpen}
			/>
		</div>
	);
};
